import Vue from 'vue';
import Router from 'vue-router';

import API from '@/api';
import environment from '@/environment';
import store from '@/store';

// Note: We may want to explore possible benefits of using dynamic
//   imports here, which will tell webpack to create separate files
//   for each route when using 'npm run build'. Example:
//
//     const UserAccount = () => import('@/pages/UserAccount');

// We include apexcharts here, which is huge (about the same size
// as Vuetify) which is why we delay its download until needed.
//
// Note: This can be problematic in a local dev setup where we have
//   two servers (dev-server on 8080 vs Django runserver on 8000). When
//   nginx is used to combine the two we need to add a location block
//   that handles URLs like '/0.js' to serve the webpack chunk files
//   that are created by this lazy import automatically.
const SpotStats = () => import('@/pages/SpotStats');

import About from '@/pages/About';
import AddContentToDecoder from '@/pages/AddContentToDecoder';
import AddDecoderToChannel from '@/pages/AddDecoderToChannel';
import AddDecoderToContentSelection from '@/pages/AddDecoderToContentSelection';
import AddDecoderToSpot from '@/pages/AddDecoderToSpot';
import AddDecoderToTicker from '@/pages/AddDecoderToTicker';
import BrandGroupSelection from '@/pages/BrandGroupSelection';
import Brandings from '@/pages/Brandings';
import CampaignCreate from '@/pages/CampaignCreate';
import CampaignDetail from '@/pages/CampaignDetail';
import Campaigns from '@/pages/Campaigns';
import ContentCreatorBilling from '@/pages/ContentCreatorBilling';
import ContentCreatorLanding from '@/pages/ContentCreatorLanding';
import ContentCreatorPlans from '@/pages/ContentCreatorPlans';
import Contents from '@/pages/Contents';
import ContentUpload from '@/pages/ContentUpload';
import Dashboard from '@/pages/Dashboard';
import DecoderDetail from '@/pages/DecoderDetail';
import Devices from '@/pages/Devices';
import Downloads from '@/pages/Downloads';
import Feedback from '@/pages/Feedback';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Maintenance from '@/pages/Maintenance';
import MenuAdditives from '@/pages/MenuAdditives';
import MenuCategories from '@/pages/MenuCategories';
import MenuCategoryDecoders from '@/pages/MenuCategoryDecoders';
import MenuItemCreateEdit from '@/pages/MenuItemCreateEdit';
import MenuItems from '@/pages/MenuItems';
import MenuPreview from '@/pages/MenuPreview';
import MenuPreviewAllScreens from '@/pages/MenuPreviewAllScreens';
import Menus from '@/pages/Menus';
import MenuStart from '@/pages/MenuStart';
import Music from '@/pages/Music';
import OpeningHours from '@/pages/OpeningHours';
import OverlayOptionsDev from '@/pages/OverlayOptionsDev';
import PasswordForgotten from '@/pages/PasswordForgotten';
import RadioChannelDetail from '@/pages/RadioChannelDetail';
import Signup from '@/pages/Signup';
import Sites from '@/pages/Sites';
import SpotCategories from '@/pages/SpotCategories';
import SpotDetail from '@/pages/SpotDetail';
import Statistics from '@/pages/Statistics';
import StoreMenu from '@/pages/StoreMenu';
import TaggableDecoders from '@/pages/TaggableDecoders';
import Tags from '@/pages/Tags';
import TickerDetail from '@/pages/TickerDetail';
import Timeframes from '@/pages/Timeframes';
import TvTemplateDetail from '@/pages/TvTemplateDetail';
import TvTemplates from '@/pages/TvTemplates';
import TwoFactorAuthentication from '@/pages/TwoFactorAuthentication';
import UserAccount from '@/pages/UserAccount';
import UserManagementInvite from '@/pages/UserManagementInvite';
import UserManagementManage from '@/pages/UserManagementManage';
import UserManagementUser from '@/pages/UserManagementUser';
import WifiSetup from '@/pages/WifiSetup';
import Workspace from '@/pages/Workspace';

Vue.use(Router);

const MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR = 'rgba(0, 0, 0, 0.04)';

const router = new Router({
  mode: 'history',
  base: environment.router.base,
  routes: [
    // No authentication needed.
    {
      path: '/landing',
      name: 'landing',
      component: Landing,
      meta: {requiresAuth: false},
    },
    {
      path: '/wifisetup',
      name: 'wifisetup',
      component: WifiSetup,
      meta: {requiresAuth: false},
    },
    {
      path: '/login',
      name: 'login',
      component: Login,
      meta: {
        requiresAuth: false,
        // Make sure the landing page is shown on mobile.
        checkAppWrapper: true,
      },
    },
    {
      path: '/signup',
      name: 'signup',
      component: Signup,
      meta: {requiresAuth: false},
    },
    {
      path: '/passwordforgotten',
      name: 'passwordforgotten',
      component: PasswordForgotten,
      meta: {requiresAuth: false},
    },
    {
      path: '/maintenance',
      name: 'maintenance',
      component: Maintenance,
      meta: {requiresAuth: false},
    },
    // Authentication needed.
    {
      path: '/account',
      name: 'account',
      component: UserAccount,
      meta: {requiresAuth: true},
    },
    {
      path: '/account/twofactor',
      name: 'twoFactorAuthentication',
      component: TwoFactorAuthentication,
      meta: {requiresAuth: true},
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: Dashboard,
      meta: {requiresAuth: true},
    },
    {
      path: '/sites',
      name: 'sites',
      component: Sites,
      meta: {requiresAuth: true},
    },
    {
      path: '/stores/:storeId/menu',
      name: 'storemenu',
      component: StoreMenu,
      meta: {
        allowFullscreen: true,
        contentAreaBackgroundColor: MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR,
        requiresAuth: true,
      },
    },
    {
      path: '/campaigns',
      name: 'campaigns',
      component: Campaigns,
      meta: {requiresAuth: true},
    },
    {
      path: '/campaigns/new',
      name: 'campaigncreate',
      component: CampaignCreate,
      meta: {
        requiresAuth: true,
        allowFullscreen: true,
      },
    },
    {
      path: '/campaigns/:campaignId',
      name: 'campaign',
      component: CampaignDetail,
      meta: {requiresAuth: true},
    },
    {
      path: '/users/invite',
      name: 'userManagementInviteNew',
      component: UserManagementInvite,
      meta: {requiresAuth: true},
    },
    {
      path: '/users/manage',
      name: 'userManagementManage',
      component: UserManagementManage,
      meta: {requiresAuth: true},
    },
    {
      path: '/users/manage/:userId',
      name: 'userManagementUser',
      component: UserManagementUser,
      meta: {requiresAuth: true},
    },
    {
      path: '/workspace',
      name: 'workspace',
      component: Workspace,
      meta: {requiresAuth: true},
    },
    {
      path: '/statistics',
      name: 'statistics',
      component: Statistics,
      meta: {requiresAuth: true},
      beforeEnter: (to, from, next) => {
        store.getters.userCanSeeContentStoreStatistics ? next() : next('/');
      },
    },
    {
      path: '/music',
      name: 'music',
      component: Music,
      meta: {
        allowFullscreen: true,
        requiresAuth: true,
      },
    },
    {
      path: '/content',
      name: 'contents',
      component: Contents,
      meta: {requiresAuth: true},
    },
    {
      path: '/upload',
      name: 'upload',
      component: ContentUpload,
      meta: {
        allowFullscreen: true,
        requiresAuth: true,
      },
    },
    {
      path: '/openinghours',
      name: 'openinghours',
      component: OpeningHours,
      meta: {requiresAuth: true},
    },
    {
      path: '/overlayoptions/dev',
      name: 'overlayoptionsdev',
      component: OverlayOptionsDev,
      meta: {requiresAuth: false},
    },
    {
      path: '/categories',
      name: 'categories',
      component: SpotCategories,
      meta: {requiresAuth: true},
    },
    {
      path: '/tags/manage',
      name: 'tagsmanage',
      component: Tags,
      meta: {
        allowFullscreen: true,
        requiresAuth: true,
      },
    },
    {
      path: '/tags/players',
      name: 'taggabledecoders',
      component: TaggableDecoders,
      meta: {
        allowFullscreen: true,
        requiresAuth: true,
      },
    },
    {
      path: '/timeframes',
      name: 'timeframes',
      component: Timeframes,
      meta: {requiresAuth: true},
    },
    {
      path: '/brands',
      name: 'brands',
      component: BrandGroupSelection,
      meta: {requiresAuth: true},
    },
    {
      path: '/ticker/:tickerId',
      name: 'ticker',
      component: TickerDetail,
      meta: {requiresAuth: true},
    },
    {
      path: '/ticker/:tickerId/add',
      name: 'adddecodertoticker',
      component: AddDecoderToTicker,
      meta: {requiresAuth: true},
    },
    {
      path: '/downloads',
      name: 'downloads',
      component: Downloads,
      meta: {requiresAuth: true},
      beforeEnter: (to, from, next) => {
        store.getters.userHasDownloads ? next() : next('/');
      },
    },
    {
      path: '/feedback',
      name: 'feedback',
      component: Feedback,
      meta: {requiresAuth: true},
    },
    {
      path: '/about',
      name: 'about',
      component: About,
      meta: {requiresAuth: true},
    },
    {
      path: '/player/:ctype/:decoderId',
      name: 'decoder',
      component: DecoderDetail,
      meta: {requiresAuth: true},
    },
    {
      path: '/player/:ctype/:decoderId/add',
      name: 'addcontenttodecoder',
      component: AddContentToDecoder,
      meta: {requiresAuth: true},
    },
    {
      path: '/devices',
      name: 'devices',
      component: Devices,
      meta: {
        requiresAuth: true,
        allowFullscreen: true,
      },
    },
    {
      path: '/spot/:ctype/:spotId', // Note: For TV, use the spot.bundle.id instead.
      name: 'spot',
      component: SpotDetail,
      meta: {requiresAuth: true},
    },
    {
      path: '/spot/:ctype/:spotId/stats',
      name: 'spotstats',
      component: SpotStats,
      meta: {requiresAuth: true},
    },
    {
      path: '/spot/:ctype/:spotId/replace',
      name: 'spotreplace',
      component: ContentUpload,
      meta: {
        requiresAuth: true,
        replace: true,
      },
    },
    {
      path: '/channel/:channelId',
      name: 'channel',
      component: RadioChannelDetail,
      meta: {requiresAuth: true},
    },
    {
      path: '/channel/:channelId/add',
      name: 'adddecodertochannel',
      component: AddDecoderToChannel,
      meta: {requiresAuth: true},
    },
    {
      path: '/spot/:ctype/:spotId/add',
      name: 'adddecodertospot',
      component: AddDecoderToSpot,
      meta: {requiresAuth: true},
    },
    {
      path: '/content/selected/add',
      name: 'adddecodertocontentselection',
      component: AddDecoderToContentSelection,
      meta: {requiresAuth: true},
    },
    {
      path: '/branding',
      name: 'branding',
      component: Brandings,
      meta: {requiresAuth: true},
    },
    {
      path: '/contentcreator/landing',
      name: 'contentcreatorlanding',
      component: ContentCreatorLanding,
      meta: {requiresAuth: true},
    },
    {
      path: '/contentcreator/plans',
      name: 'contentcreatorplans',
      component: ContentCreatorPlans,
      meta: {requiresAuth: true},
    },
    {
      path: '/contentcreator/billing',
      name: 'contentcreatorbilling',
      component: ContentCreatorBilling,
      meta: {requiresAuth: true},
    },
    {
      path: '/contentcreator/templates',
      name: 'tvtemplates',
      component: TvTemplates,
      meta: {requiresAuth: true},
    },
    {
      path: '/contentcreator/templates/:tvtemplateId',
      name: 'tvtemplate',
      component: TvTemplateDetail,
      meta: {requiresAuth: true},
    },
    {
      path: '/menus',
      name: 'menus',
      component: Menus,
      meta: {
        allowFullscreen: true,
        contentAreaBackgroundColor: MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR,
        requiresAuth: true,
      },
    },
    {
      path: '/menus/:menuId/start',
      name: 'menustart',
      component: MenuStart,
      meta: {
        allowFullscreen: true,
        contentAreaBackgroundColor: MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR,
        requiresAuth: true,
      },
    },
    {
      path: '/menus/:menuId/additives',
      name: 'menuadditives',
      component: MenuAdditives,
      meta: {
        allowFullscreen: true,
        contentAreaBackgroundColor: MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR,
        requiresAuth: true,
      },
    },
    {
      path: '/menus/:menuId/categories',
      name: 'menucategories',
      component: MenuCategories,
      meta: {
        allowFullscreen: true,
        contentAreaBackgroundColor: MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR,
        requiresAuth: true,
      },
    },
    {
      path: '/menus/:menuId/categories/players',
      name: 'menucategorydecoders',
      component: MenuCategoryDecoders,
      meta: {
        allowFullscreen: true,
        contentAreaBackgroundColor: MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR,
        requiresAuth: true,
      },
    },
    {
      path: '/menus/:menuId/preview',
      name: 'menupreview',
      component: MenuPreview,
      meta: {
        allowFullscreen: true,
        contentAreaBackgroundColor: MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR,
        requiresAuth: true,
      },
    },
    {
      path: '/menus/:menuId/preview/allscreens',
      name: 'menupreviewallscreens',
      component: MenuPreviewAllScreens,
      meta: {
        allowFullscreen: true,
        requiresAuth: true,
      },
    },
    {
      path: '/menus/:menuId/items',
      name: 'menuitems',
      component: MenuItems,
      meta: {
        allowFullscreen: true,
        contentAreaBackgroundColor: MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR,
        requiresAuth: true,
      },
    },
    {
      path: '/menus/:menuId/items/create',
      name: 'createmenuitem',
      component: MenuItemCreateEdit,
      meta: {
        allowFullscreen: true,
        contentAreaBackgroundColor: MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR,
        requiresAuth: true,
        transition: 'page-slide-x-transition',
      },
    },
    {
      path: '/menuitems/:menuItemId/edit',
      name: 'editmenuitem',
      component: MenuItemCreateEdit,
      meta: {
        allowFullscreen: true,
        contentAreaBackgroundColor: MENU_PAGES_CONTENT_AREA_BACKGROUND_COLOR,
        requiresAuth: true,
        transition: 'page-slide-x-transition',
      },
    },
    {
      path: '/_clearall',
      name: '_clearall',
    },
    // Fallback to login.
    {
      path: '*',
      redirect: '/login',
    },
  ],
});

// TODO: This is hard to maintain. Better could be: make beforeEach() an
//   async function, so that it can await a call like isAuthenticated()
//   that may fetch user details from the server (if not already available).
//   That way we could delay routing (aka calling next()) until then,
//   enabling us to route to protected routes like /users/manage on
//   initial page load, which currently is not possible (as it fails to
//   check if the user is allowed to see that page, since we have no
//   user details fetched yet).

// Navigation guards.
router.beforeEach((to, from, next) => {
  if (to.name === '_clearall') {
    API.deleteCookie('authtoken');
    localStorage.clear();
    return next('/login');
  }

  // Components may have added a question to be confirmed by the user
  // to prevent accidental route changes.
  if (store.state.common.preRouteQuestion) {
    let confirmed = confirm(store.state.common.preRouteQuestion);
    if (!confirmed) {
      if (store.state.common.unsetPreRouteQuestionOnNotConfirmed) {
        store.commit('SET_UNSET_PRE_ROUTE_QUESTION_ON_NOT_CONFIRMED', false);
        store.commit('UNSET_PRE_ROUTE_QUESTION');
      }
      return;
    } else {
      store.commit('UNSET_PRE_ROUTE_QUESTION');
    }
  }

  let authToken = store.getAuthToken();

  // Password reset does not need authentication, in fact it requires
  // to be logged out. Signup follows the same logic.
  if (
    to.path == '/passwordforgotten' ||
    to.path == '/landing' ||
    to.path == '/signup'
  ) {
    if (authToken !== null) {
      let logoutTo = '/login';
      if (to.path == '/signup') {
        logoutTo = to.path;
      }
      API.logout(logoutTo);
      return;
    } else {
      return next();
    }
  }

  // If already logged in, go to the dashboard instead, unless the
  // 'forcelogout' query param is given, on which we do just that.
  // This is useful when being redirected to the login meant for a
  // specific user to ensure we don't end up in possibly some other
  // user's app session.
  if (to.path === '/login') {
    if (to.query.forcelogout) {
      if (authToken) {
        API.logout();
      }
      return next();
    }
    if (authToken) {
      return next('/dashboard');
    }
  }

  if (store.state.common.fullscreenMode && !to.meta.allowFullscreen) {
    store.commit('SET_FULLSCREEN_MODE', false);
  }

  // Where are you going, login first!
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  if (requiresAuth && !authToken) {
    if (to.path !== '/login') {
      return next({
        path: '/login',
        query: {
          redirect:
            to.path +
            (to.query ? '?' + new URLSearchParams(to.query).toString() : ''),
        },
      });
    }
  }

  return next();
});

// Capture routing history.
router.afterEach((to, from) => {
  store.commit('APPEND_ROUTE_TO_HISTORY', to);
});

router.scrollBehavior = (to, from, savedPosition) => {
  return savedPosition;
};

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js